import { useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { baseWhere } from 'graph/apollo-util';
import GET_KEYBOARDING_CURRICULUMS from 'graph/resource/get-keyboarding-curriculum-query';
import useProductCache from 'store/cache/product-cache';
import { KeyboardingCurriculum } from 'types/schema-generated-types';
import { LessonPreviewWeekData } from '../types';
import { transformLessonPlan } from '../util/lesson-plan-tranformer';
import { flattenLessonPlanData } from '../util/lesson-plan-util';

const useKeyLessonPreview = () => {
  const [{ findProductCurriculumByCode }] = useProductCache();
  const { resourceTypeId, productCurriculumCode, week } = useParams<{
    resourceTypeId: string;
    productCurriculumCode: string;
    week: string;
  }>();

  const [
    getKeyboardingCurriculums,
    { data: keyboardingCurriculumData, loading, error }
  ] = useLazyQuery<Record<'keyboardingCurriculums', KeyboardingCurriculum[]>>(
    GET_KEYBOARDING_CURRICULUMS
  );

  const productCurriculum = findProductCurriculumByCode(productCurriculumCode);
  const gradeLevel = productCurriculum?.gradeLevel?.[0];

  useEffect(() => {
    if (!productCurriculum || !gradeLevel) return;

    getKeyboardingCurriculums(baseWhere({ productCurriculumId: productCurriculum.id }));
  }, [getKeyboardingCurriculums, productCurriculum, gradeLevel]);

  const buildFlattenedData: () => LessonPreviewWeekData[] = useCallback(() => {
    const keyboardCurriculum = keyboardingCurriculumData?.keyboardingCurriculums[0];

    return flattenLessonPlanData(
      keyboardCurriculum?.curriculumActivities?.map(transformLessonPlan)
    );
  }, [keyboardingCurriculumData]);

  return {
    data: buildFlattenedData(),
    loading,
    error,
    gradeLevel,
    productCurriculumCode,
    resourceTypeId,
    week
  };
};

export default useKeyLessonPreview;
